import React, { useState } from "react";
import { Button, Input, Popconfirm, Select, Table, message } from "antd";

import * as api from "../controller";
import TaxLinesEditor from "./TaxLinesEditor";
import { formLines, linesText, sourceText } from "./taxMath";
import "../leadSettings.css";
import "./billing.css";

const ok = (r) => r && r.status >= 200 && r.status < 300;

/**
 * Users & Billing → Sales tax, for one office (`officeId`) or a whole practice
 * (`practiceId`). `summary` is the `sales_tax` block of the billing payload;
 * every save answers a fresh one, which `onChanged(summary)` hands back.
 *
 * An office: what it is charged and why, its province (its own Office.state —
 * correcting it here corrects it for the SunLife agent too), and its own rule.
 * A practice: its rule, which covers every office without one, and each office's
 * tax. Rule changes apply to NEW charges; a plan already bought keeps its tax.
 */
export default function SalesTaxCard({ officeId, practiceId, summary, onChanged }) {
  const [busy, setBusy] = useState(false);
  if (!summary) return null;

  const save = async (patch, done) => {
    setBusy(true);
    const r = officeId
      ? await api.updateOfficeSalesTax(officeId, patch)
      : await api.updatePracticeSalesTax(practiceId, patch);
    setBusy(false);
    if (!ok(r)) {
      message.error((r.json && r.json.detail) || "Couldn't save the sales tax.");
      return false;
    }
    message.success(done);
    onChanged(r.json);
    return true;
  };

  if (!officeId) {
    return (
      <>
        <RuleRow
          noun="practice"
          rule={summary.practice_rule}
          busy={busy}
          onSave={(rule) => save({ rule }, "Practice sales tax saved.")}
          onRemove={() => save({ rule: null }, "Practice sales tax removed — each office pays its province's rate.")}
        />
        <Table
          rowKey="office_id"
          size="small"
          pagination={false}
          dataSource={summary.offices || []}
          locale={{ emptyText: <div className="crm-empty">No offices yet.</div> }}
          columns={[
            { title: "Office", dataIndex: "office_name", key: "office" },
            { title: "Province", key: "province", width: 150,
              render: (_, r) => (r.province ? r.province.name : <span className="ub-warn-text">Unknown</span>) },
            { title: "Charged", key: "lines", render: (_, r) => linesText(r.lines) },
            { title: "Why", key: "source", render: (_, r) => <span className="leadset-hint">{sourceText(r)}</span> },
          ]}
        />
      </>
    );
  }

  const province = summary.province;
  return (
    <div className="leadset-panel" style={{ borderTop: "none" }}>
      <div className="leadset-row">
        <div className="leadset-row-label">
          {linesText(summary.lines)}
          <div className="leadset-hint">{sourceText(summary)}</div>
        </div>
      </div>
      {summary.plan && summary.plan.differs ? (
        <div className="ub-callout">
          Their plan ({summary.plan.tier_title}) was bought at {linesText(summary.plan.lines)} and keeps it at every
          renewal. Today&rsquo;s rate applies to new charges: extra requests, plan changes and new purchases.
        </div>
      ) : null}
      <div className="leadset-row">
        <div className="leadset-row-label">
          Province
          <div className="leadset-hint">
            Read from the office&rsquo;s address (&ldquo;{summary.state || "blank"}&rdquo;). Changing it here also
            changes the province the SunLife agent uses.
          </div>
        </div>
        <div className="leadset-row-control">
          <Select
            style={{ width: 210 }}
            placeholder="Unknown — choose"
            value={province ? province.code : undefined}
            disabled={busy}
            onChange={(code) => save({ province: code }, "Province saved.")}
            options={(summary.provinces || []).map((p) => ({ value: p.code, label: p.name }))}
            dropdownStyle={{ zIndex: 10060 }}
          />
        </div>
      </div>
      <RuleRow
        noun="office"
        rule={summary.office_rule}
        busy={busy}
        onSave={(rule) => save({ rule }, "This office's sales tax saved.")}
        onRemove={() => save({ rule: null }, "Office sales tax removed.")}
      />
    </div>
  );
}

/** A practice's or office's own rule: shown, edited (lines + reason) or removed. */
function RuleRow({ noun, rule, busy, onSave, onRemove }) {
  const [editing, setEditing] = useState(null); // {lines, reason} while the form is open

  const open = () => setEditing({
    lines: rule ? formLines(rule.lines) : [{ label: "", percent: "" }],
    reason: rule ? rule.reason : "",
  });
  const submit = async () => {
    if (await onSave({ lines: editing.lines, reason: editing.reason })) setEditing(null);
  };

  if (editing) {
    return (
      <div className="ub-tax-form">
        <div className="ub-fact-label">Tax for this {noun}</div>
        <TaxLinesEditor value={editing.lines} onChange={(lines) => setEditing({ ...editing, lines })} allowNone />
        <Input.TextArea
          placeholder={`Why this ${noun} is taxed differently (shown here and on the Sales tax list)`}
          value={editing.reason}
          maxLength={255}
          autoSize={{ minRows: 1, maxRows: 3 }}
          onChange={(e) => setEditing({ ...editing, reason: e.target.value })}
        />
        <div className="ub-tax-form-actions">
          <Button size="small" onClick={() => setEditing(null)}>Cancel</Button>
          <Button size="small" type="primary" loading={busy} onClick={submit}>Save</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="leadset-row">
      <div className="leadset-row-label">
        {rule ? `This ${noun}: ${linesText(rule.lines)}` : `No ${noun} rate`}
        <div className="leadset-hint">
          {rule
            ? `${rule.reason}${rule.updated_by ? ` · set by ${rule.updated_by}` : ""}`
            : noun === "practice"
              ? "Each office pays its province's rate unless the practice or the office has its own."
              : "Pays the practice's or the province's rate. Set one only for a real exception, e.g. tax-exempt."}
        </div>
      </div>
      <div className="leadset-row-control">
        <Button size="small" disabled={busy} onClick={open}>{rule ? "Edit" : `Set a ${noun} rate`}</Button>
        {rule ? (
          <Popconfirm
            title={`Remove this ${noun}'s rate?`}
            okText="Remove"
            onConfirm={onRemove}
            zIndex={10060}
          >
            <Button size="small" danger disabled={busy}>Remove</Button>
          </Popconfirm>
        ) : null}
      </div>
    </div>
  );
}
